import { useCart } from '../../../../../../app/_providers/CartProvider.client'
import { useConsentPolicy } from '../../../../../../app/_providers/ConsentPolicyProvider.client'
import { useFeatureFlag } from '../../../../../../app/_providers/ExperimentationProvider.client'

import { useIsAffirmEligible } from './useIsAffirmEligible'

const AFFIRM_MIN_CART_CENTS = 10000 as const

/**
 * Determines if the user should be shown the Affirm promo messaging in Minicart based on:
 * - user eligibility (see `useIsAffirmEligible`)
 * - cart eligibility (>= $100 cart total)
 * - functional consent (Affirm's SDK is a functional cookie; without it the modal can't load)
 * - feature flag bucketing
 */
export function useAffirmExperiment({ isRevealed }: { isRevealed: boolean }) {
  const isAffirmEligible = useIsAffirmEligible()
  const {
    consentPolicy: { functional },
  } = useConsentPolicy()
  const {
    cart: { totalPrice },
  } = useCart()
  const hasMetMinimumCartTotal = totalPrice >= AFFIRM_MIN_CART_CENTS
  const hasFunctionalConsent = functional === 'allowed'
  const hasAffirmConfig = Boolean(
    process.env.NEXT_PUBLIC_AFFIRM_API_KEY && process.env.NEXT_PUBLIC_AFFIRM_CDN_BASE_URL
  )

  const decision = useFeatureFlag<'control' | 'variant'>({
    key: 'affirm-bnpl-messaging',
    controlVariant: 'control',
    ifAccessedPriorToDecisionInitialization: 'return-null-while-pending',
    skip:
      !isRevealed ||
      !isAffirmEligible ||
      !hasMetMinimumCartTotal ||
      !hasFunctionalConsent ||
      !hasAffirmConfig,
  })

  return {
    showPromo: decision === 'variant',
  }
}
